import { useState } from 'react';
import { useIntl } from 'react-intl';
import CopyToClipboardButton from '@rossum/components/copy-to-clipboard/CopyToClipboardButton';
import { LabelChip } from '@rossum/components/labels/LabelChip';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Tile } from '@rossum/components/tile';
import { EmptyStateRadar } from '@rossum/ui';
import { IconEdit, IconTrash } from '@rossum/ui/icons/tabler';
import {
  CircularProgress,
  Dialog,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { useRequestUnpaginatedLabels } from '../hooks/useRequestLabels';
import { DeleteLabelDialog, EditLabelDialog } from './LabelDialogs';
import { LabelsHeader } from './LabelsHeader';

const LOADER_HEIGHT = 600;
const COPY_BUTTON_CLASSNAME = 'label-id-copy-button';

type LabelActionState = {
  id: number;
  name: string;
  color?: string | null;
  action: 'delete' | 'edit';
};

export const LabelList = () => {
  const [labelAction, setLabelAction] = useState<LabelActionState | null>(null);

  const intl = useIntl();

  const {
    data: labels,
    isInitialLoading: isLoading,
    isSuccess,
    isError,
  } = useRequestUnpaginatedLabels();

  const handleOnClose = () => setLabelAction(null);

  const getDialogContent = () => {
    if (!labelAction) return null;

    const { id, name, action, color } = labelAction;

    if (action === 'delete') {
      return <DeleteLabelDialog id={id} name={name} onClose={handleOnClose} />;
    }

    return (
      <EditLabelDialog
        id={id}
        name={name}
        color={color}
        onClose={handleOnClose}
      />
    );
  };

  const getContent = () => {
    if (isLoading) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          height={LOADER_HEIGHT}
        >
          <CircularProgress />
        </Stack>
      );
    }

    if (isError) {
      return (
        <Stack height="50vh">
          <EmptyStateRadar
            title={intl.formatMessage({
              id: 'containers.settings.labels.fetchFailed',
            })}
          />
        </Stack>
      );
    }

    if (isSuccess) {
      if (labels.length === 0) {
        return (
          <Stack height="50vh">
            <EmptyStateRadar
              title={intl.formatMessage({
                id: 'containers.settings.labels.noLabels',
              })}
            />
          </Stack>
        );
      }

      return (
        <Stack direction="column" spacing={0.5}>
          {labels.map(label => (
            <Stack
              key={label.id}
              sx={{
                [`& .${COPY_BUTTON_CLASSNAME}`]: { visibility: 'hidden' },
                '&:hover': {
                  [`& .${COPY_BUTTON_CLASSNAME}`]: { visibility: 'visible' },
                },
              }}
            >
              <Tile
                title={<LabelChip name={label.name} color={label.color} />}
                description={
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {intl.formatMessage({
                        id: 'containers.settings.labels.labelId',
                      })}
                      {` ${label.id}`}
                    </Typography>
                    <CopyToClipboardButton
                      buttonProps={{ className: COPY_BUTTON_CLASSNAME }}
                      content={label.id.toString()}
                      iconColor="secondary"
                    />
                  </Stack>
                }
                quickActions={[
                  {
                    key: 'edit',
                    iconProps: {
                      Icon: IconEdit,
                      state: false,
                      disabled: false,
                    },
                    onClick: () =>
                      setLabelAction({
                        id: label.id,
                        name: label.name,
                        color: label.color,
                        action: 'edit',
                      }),
                  },
                  {
                    key: 'delete',
                    iconProps: {
                      Icon: IconTrash,
                      state: false,
                      disabled: false,
                    },
                    onClick: () =>
                      setLabelAction({
                        id: label.id,
                        name: label.name,
                        action: 'delete',
                      }),
                  },
                ]}
                dragHandle={null}
              />
            </Stack>
          ))}
        </Stack>
      );
    }

    return null;
  };

  return (
    <PageLayout
      renderHeader={params => (
        <LabelsHeader {...params} labelsCount={labels?.length ?? 0} />
      )}
    >
      <Stack px={4} py={4}>
        {getContent()}
      </Stack>
      <Dialog open={!!labelAction} onClose={handleOnClose}>
        {getDialogContent()}
      </Dialog>
    </PageLayout>
  );
};
